/**
* @license Apache-2.0
*
* Copyright (c) 2026 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import numelDimension from '@stdlib/ndarray/base/numel-dimension';
import getStride from '@stdlib/ndarray/base/stride';
import getOffset from '@stdlib/ndarray/base/offset';
import getData from '@stdlib/ndarray/base/data-buffer';
import { ndarray as strided } from './../../../../strided/nanmskmidrange';


// MAIN //

/**
* Computes the mid-range of a one-dimensional ndarray according to a mask, ignoring `NaN` values.
*
* ## Notes
*
* -   The function expects the following ndarrays:
*
*     -   a one-dimensional input ndarray.
*     -   a one-dimensional mask ndarray.
*
* @param {ArrayLikeObject<Object>} arrays - array-like object containing ndarrays
* @returns {number} mid-range
*
* @example
* import vector from '@stdlib/ndarray/vector/ctor';
*
* var x = vector( [ 1.0, -2.0, 4.0, 2.0, NaN ], 'generic' );
* var mask = vector( [ 0, 0, 1, 0, 0 ], 'uint8' );
*
* var v = nanmskmidrange( [ x, mask ] );
* // returns 0.0
*/
function nanmskmidrange( arrays ) {
	var mask = arrays[ 1 ];
	var x = arrays[ 0 ];
	return strided( numelDimension( x, 0 ), getData( x ), getStride( x, 0 ), getOffset( x ), getData( mask ), getStride( mask, 0 ), getOffset( mask ) ); // eslint-disable-line max-len
}


// EXPORTS //

export default nanmskmidrange;
